/**
 * AdminDataHygieneCard — a super-admin operational tool for purging
 * demo/test data and residual expert-tier legacy artefacts from the DB.
 *
 * Two-step flow:
 *   1. Preview (dry_run=true) — shows counts + a sample of what WOULD be
 *      deleted. No mutation.
 *   2. Confirm & Purge (dry_run=false) — user must click a second time.
 *      Whitelisted official demo accounts are ALWAYS preserved.
 */
import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { AlertTriangle, ShieldCheck, RotateCw, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function AdminDataHygieneCard() {
    const [preview, setPreview] = useState(null);
    const [busy, setBusy] = useState(false);
    const [confirmed, setConfirmed] = useState(false);

    async function loadPreview() {
        setBusy(true);
        try {
            const { data } = await api.post("/admin/ops/purge-demo", { dry_run: true });
            setPreview(data);
            setConfirmed(false);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Preview failed");
        } finally { setBusy(false); }
    }

    async function purgeReal() {
        if (!confirmed) return;
        setBusy(true);
        try {
            const { data } = await api.post("/admin/ops/purge-demo", { dry_run: false });
            setPreview(data);
            toast.success(`Purge complete · ${data.deleted_totals?.users || 0} users deleted`);
            setConfirmed(false);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Purge failed");
        } finally { setBusy(false); }
    }

    return (
        <div className="space-y-4" data-testid="admin-data-hygiene">
            <div className="border border-border bg-card rounded p-4">
                <div className="flex items-start gap-3 mb-3">
                    <ShieldCheck className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                    <div>
                        <div className="text-sm font-extrabold">Demo & test data purge</div>
                        <p className="text-xs text-muted-foreground mt-0.5">
                            Removes lingering test/audit accounts (patterns like <code className="mono">audit_</code>,
                            <code className="mono"> ephemeral_</code>, <code className="mono">@test.example</code>, etc.)
                            plus orphan agencies. Cascades to notifications, chats, positions, trades,
                            recovery codes, and login attempts. The official demo accounts
                            (admin, trader, leader, follower, broker, moe_admin, kimani.pro) are
                            ALWAYS preserved.
                        </p>
                    </div>
                </div>
                <div className="flex gap-2 flex-wrap">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={loadPreview}
                        disabled={busy}
                        data-testid="hygiene-preview-btn"
                    >
                        <RotateCw className="w-3.5 h-3.5 mr-1.5" />
                        {busy && !preview ? "Loading…" : "Preview purge"}
                    </Button>
                    {preview && (
                        <>
                            {!confirmed ? (
                                <Button
                                    variant="destructive"
                                    size="sm"
                                    onClick={() => setConfirmed(true)}
                                    disabled={busy || (preview.user_candidates || 0) === 0}
                                    data-testid="hygiene-confirm-btn"
                                >
                                    <AlertTriangle className="w-3.5 h-3.5 mr-1.5" />
                                    I understand — enable purge
                                </Button>
                            ) : (
                                <Button
                                    variant="destructive"
                                    size="sm"
                                    onClick={purgeReal}
                                    disabled={busy}
                                    data-testid="hygiene-purge-btn"
                                >
                                    <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                                    {busy ? "Purging…" : "Purge now"}
                                </Button>
                            )}
                        </>
                    )}
                </div>
            </div>

            {preview && (
                <div className="border border-border bg-card rounded p-4" data-testid="hygiene-preview-panel">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                        <Stat label="User candidates" value={preview.user_candidates} />
                        <Stat label="Orphan agencies" value={preview.orphan_agencies} />
                        <Stat label="Contact submissions" value={preview.contact_submissions} />
                        <Stat label="Whitelisted (preserved)" value={preview.whitelist_preserved?.length || 0} />
                    </div>

                    {preview.deleted_totals && (
                        <div className="border-t border-border pt-3 mb-3">
                            <div className="overline mb-1 text-emerald-600 dark:text-emerald-400">Purge summary</div>
                            <div className="text-xs mono grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1">
                                {Object.entries(preview.deleted_totals).map(([k, v]) => (
                                    <div key={k} className="flex justify-between">
                                        <span className="text-muted-foreground">{k}</span>
                                        <span className="font-bold">{v}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="grid md:grid-cols-2 gap-4">
                        <div>
                            <div className="overline mb-1">Sample users (first 30)</div>
                            <div className="max-h-64 overflow-y-auto text-[11px] mono border border-border rounded bg-background p-2 space-y-0.5">
                                {(preview.sample_users || []).map((u) => (
                                    <div key={u.id} className="truncate" title={u.email}>
                                        {u.email} <span className="text-muted-foreground">· {u.tier || u.role}</span>
                                    </div>
                                ))}
                                {(preview.sample_users?.length || 0) === 0 && (
                                    <div className="text-muted-foreground">No candidates — DB is clean.</div>
                                )}
                            </div>
                        </div>
                        <div>
                            <div className="overline mb-1">Orphan agencies (first 20)</div>
                            <div className="max-h-64 overflow-y-auto text-[11px] mono border border-border rounded bg-background p-2 space-y-0.5">
                                {(preview.sample_orphan_agencies || []).map((a) => (
                                    <div key={a.id} className="truncate">
                                        {a.agency_name} <span className="text-muted-foreground">· {a.institution_type}</span>
                                    </div>
                                ))}
                                {(preview.sample_orphan_agencies?.length || 0) === 0 && (
                                    <div className="text-muted-foreground">None found.</div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function Stat({ label, value }) {
    return (
        <div className="border border-border rounded p-3">
            <div className="overline">{label}</div>
            <div className="text-xl font-extrabold mono">{value ?? 0}</div>
        </div>
    );
}
